// App.js
import React from 'react';
import { BrowserRouter as Router, Navigate, Route, Routes } from 'react-router-dom';
import Login from './components/Login';
import Logout from './components/Logout';
import Register from './components/Register';
import Dashboard from './components/Dashboard';
import PrivateRoute from './components/PrivateRoute';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { FeaturesProvider } from './contexts/FeaturesContext';
import { MessageProvider } from './contexts/MessageContext';
import GlobalMessage from './components/GlobalMessage';
import NavBar from './components/NavVar';
import GlobalSearch from './components/GlobalSearch';
import InventoryList from './components/InventoryList';
import InventoryAdd from './components/InventoryAdd';
import InventoryEdit from "./components/InventoryEdit";
import OrdersList from './components/OrdersList';
import OrderDetails from './components/OrderDetails';
import ProfileData from './components/profile';
import CategoryConfig from './components/CategoryConfig';
import CustomerList from './components/CustomerList';
import CustomerNew from './components/CustomerNew';
import CustomerOrders from './components/CustomerOrders';
import AccountList from './components/AccountsList';
import AccountsNew from './components/AccountsNew';
import CreateInvoice from './components/CreateInvoice';
import InvoiceList from './components/InvoiceList';
import InvoiceView from './components/InvoiceView';
import OrderForm from './components/OrderForm';
import PaymentsList from './components/Payment/PaymentsList';
import PaymentForm from './components/Payment/PaymentForm';
import ExpenseForm from './components/Expense/ExpenseForm';
import ExpenseList from './components/Expense/ExpenseList';
import CustomerEdit from './components/CustomerEdit';
import CustomerView from './components/CustomerView';
import InventoryCalendar from './components/InventoryCalendar';
import QuotesList from './components/Quote/QuoteList';
import QuoteForm from './components/Quote/QuoteForm';
import QuotePublicView from './components/Quote/QuotePublicView';
import ContractPublicView from './components/Quote/ContractPublicView';
import ContractList from './components/Quote/ContractList';
import InvoicePublicView from './components/InvoicePublicView';
import InOut from './components/widgets/InOut';
import EnquiryList from './components/Enquiry/EnquiryList';
import EnquiryView from './components/Enquiry/EnquiryView';
import EnquiryNew from './components/Enquiry/EnquiryNew';
import ProfitReport from './components/ProfitReport';
import SettingsPage from './components/Settings/SettingsPage';
import GalleryManagement from './components/GalleryManagement';
import PlaidBankConnect from './components/PlaidBankConnect';
import Reconciliation from './components/Reconciliation';
import TimesheetsPage from './components/TimesheetsPage';
import ExtensionsPage from './components/ExtensionsPage';
import ConversationsPage from './components/Sms/ConversationsPage';


import './App.css';
import './index.css';

// Add a loading component
const LoadingSpinner = () => (
  <div className="flex justify-center items-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
  </div>
);

const AppContent = () => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingSpinner/>; // Wait until auth status is known

  return (
    <>
      <GlobalMessage />
      {user && (
        <>
          <NavBar />
          <GlobalSearch />
        </>
      )}
      <main className={user ? 'app-auth-content' : 'app-public-content'}>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<Login />} />
          <Route path="/login" element={<Login />} />
          <Route path="/quote/view/:token" element={<QuotePublicView />} />
          <Route path="/contract/view/:token" element={<ContractPublicView />} />
          <Route path="/invoice/view/:token" element={<InvoicePublicView />} />

          {/* Protected Routes */}
          <Route element={<PrivateRoute />}>
            <Route path="/register" element={<Register />} />
            <Route path="/logout" element={<Logout />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/dashboard/widgets/inout" element={<InOut />} />
            <Route path="/inventory-calendar" element={<InventoryCalendar />} />
            <Route path="/inventory-availability" element={<Navigate to="/inventory-calendar" replace />} />
            <Route path="/inventory" element={<InventoryList />} />
            <Route path="/inventory/new" element={<InventoryAdd />} />
            <Route path="/inventory/:id/edit" element={<InventoryEdit />} />
            <Route path="/orders" element={<OrdersList />} />
            <Route path="/orders/new" element={<OrderForm />} />
            <Route path="/orders/customer/:customerId/new" element={<OrderForm />} />
            <Route path="/orders/:uuid" element={<OrderDetails />} />
            <Route path="/orders/:uuid/edit" element={<OrderForm />} />
            
            <Route path="/categories" element={<CategoryConfig />} />
            <Route path="/accouonts" element={<AccountList />} />
            <Route path="/accouonts/new" element={<AccountsNew />} />
            <Route path="/invoice" element={<InvoiceList />} /> 
            <Route path="/invoice/new" element={<CreateInvoice />} />
            <Route path="/invoice/:uuid/edit" element={<CreateInvoice />} />
            <Route path="/invoice/:uuid" element={<InvoiceView />} />
            <Route path="/CustomerList" element={<CustomerList />} />
            <Route path="/customer/new" element={<CustomerNew />} />
            <Route path="/customer/:uuid" element={<CustomerEdit />} />
            <Route path="/customer/:uuid/view" element={<CustomerView />} />
            <Route path="/expense/new" element={<ExpenseForm />} />
            <Route path="/expense" element={<ExpenseList />} />
            <Route path="/expense/:uuid" element={<ExpenseForm />} />
            <Route path="/customerorders/:uuid" element={<CustomerOrders />} />
            <Route path="/payment" element={<PaymentsList />} />
            <Route path="/payment/new" element={<PaymentForm />} />
            <Route path="/payment/:uuid" element={<PaymentForm />} />
            <Route path="/profile" element={<ProfileData />} />
            <Route path="/quotes" element={<QuotesList />} />
            <Route path="/quotes/:uuid" element={<QuoteForm />} />
            <Route path="/quotes/new" element={<QuoteForm />} />
            <Route path="/contracts" element={<ContractList />} />
            <Route path="/enquiries" element={<EnquiryList />} />
            <Route path="/enquiries/:uuid" element={<EnquiryView />} />
            <Route path="/enquiries/new" element={<EnquiryNew />} />
            <Route path="/reports/profit-report" element={<ProfitReport />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/settings/gallery" element={<GalleryManagement />} />
            <Route path="/extensions" element={<ExtensionsPage />} />
            <Route path="/extensions/:name" element={<ExtensionsPage />} />
            <Route path="/conversations" element={<ConversationsPage />} />
            <Route path="/bank-connections" element={<PlaidBankConnect />} />
            <Route path="/reconciliation" element={<Reconciliation />} />
            <Route path="/timesheets" element={<TimesheetsPage />} />
          </Route>
        </Routes>
      </main>
    </>
  );
};

const App = () => {
  return (
    <AuthProvider>
      <FeaturesProvider>
        <MessageProvider>
          <Router>
            <AppContent />
          </Router>
        </MessageProvider>
      </FeaturesProvider>
    </AuthProvider>
  );
};

export default App;